import React, { useMemo } from 'react';
import Modal, { ModalBody } from '../Modal';
import useTablePages from '../../hooks/useTablePages';
import {
  AssemblyItem,
  GET_ASSEMBLIES_WATER_SUPPLIER,
  GET_ASSEMBLY_IDS,
} from '../../pages/assemblies/Assemblies.query';
import TableWindow from '../Table/TableWindow';
import availableColumns from '../../pages/assemblies/Assemblies.columns';
import { useQuery } from '@apollo/client';
import ModalHeader from '../Modal/ModalHeader';
import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import useMode from '../../hooks/useMode';
import useScrolBarSize from '../../hooks/useScrollbarSize';
import { gql } from '@/modes/waterSupplier/gql';

export const GET_TEST_DRILLDOWN_IDS = gql(`
  query AnnualReportDrilldownAssemblyIds(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    backflowTests(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      groupBy: ["jobId"]
    ) {
      backflowAssemblyId
    }
  }
`);

export const GET_ASSEMBLY_DRILLDOWN_IDS = gql(`
  query AnnualReportDrilldownAssemblyIds(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    backflowAssemblies(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      groupBy: ["id"]
    ) {
      id
    }
  }
`);

type AnnualReportDrilldownModalProps = {
  title: string;
  where: Record<string, any>;
  type: 'assemblies' | 'tests';
  onClose?: () => void;
  onRowClick?: (assembly: AssemblyItem) => void;
};

const AnnualReportDrilldownModal: React.FC<AnnualReportDrilldownModalProps> = ({
  title,
  onClose,
  where,
  type,
}) => {
  const { data: assemblyIdsData } = useQuery<
    | {
        backflowTests: { backflowAssemblyId: string }[];
        backflowAssemblies: undefined;
      }
    | {
        backflowAssemblies: { id: string }[];
        backflowTests: undefined;
      }
  >(type === 'tests' ? GET_TEST_DRILLDOWN_IDS : GET_ASSEMBLY_DRILLDOWN_IDS, {
    variables: {
      where,
      limit: null,
    },
    fetchPolicy: 'no-cache',
  });

  const assembliesWhere = useMemo(() => {
    const allIds =
      assemblyIdsData?.backflowTests?.map(
        (backflowTest) => backflowTest.backflowAssemblyId
      ) ??
      assemblyIdsData?.backflowAssemblies?.map(
        (backflowAssembly) => backflowAssembly.id
      ) ??
      [];
    return {
      id: Array.from(new Set(allIds).keys()),
    };
  }, [assemblyIdsData]);

  const { loading, itemIds, itemLoader } = useTablePages<AssemblyItem>(
    'backflowAssemblies',
    GET_ASSEMBLY_IDS,
    {
      where: assembliesWhere,
      limit: null,
      orderBy: [{ col: 'type', dir: 'ASC' }],
    },
    GET_ASSEMBLIES_WATER_SUPPLIER,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
    })
  );

  const { onRowClick } = useThirdPaneCallbacks(
    `/${useMode()}/assemblies`,
    itemIds
  );

  return (
    <Modal onClose={onClose}>
      <ModalHeader title={title} />
      <ModalBody p={4}>
        {assembliesWhere.id.length === 0 ||
        (loading && itemIds.length === 0) ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={availableColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onRowClick={onRowClick}
            height="calc(100vh - 300px)"
            minHeight={48 + 32 + 35 + 2 + useScrolBarSize()}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default AnnualReportDrilldownModal;
